import {Injectable} from '@angular/core';
import { ApiService } from "./api.service";
import { DeviceDto } from '../entities/DeviceDto';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription, timer } from 'rxjs';
import { map, tap, switchMap, take, catchError, exhaustMap } from 'rxjs/operators';
import { AppSettings } from 'src/config/app-setting';
import { HistoryEntryDto } from '../entities/OperationLogDto';
import { HistorySearch } from '../entities/HistorySearch';

@Injectable({
  providedIn: 'root'
})
export class DataService {

  private devicesSubject = new BehaviorSubject<DeviceDto[]>([]);
  devices$ = this.devicesSubject.asObservable();
  private devicesPollingSub?: Subscription;

  private historyEntriesSubject = new BehaviorSubject<HistoryEntryDto[]>([]);
  historyEntries$ = this.historyEntriesSubject.asObservable();
  private historyEntriesPollingSub?: Subscription;

  private refreshTrigger$ = new Subject<void>();
  
  private searchSubject = new BehaviorSubject<HistorySearch>({
    startDate: null,
    endDate: null,
    licensePlate: '',
    userEmail: ''
  });

  setSearch(search: HistorySearch) {
    this.searchSubject.next(search);
    this.refreshTrigger$.next();
  }

  constructor(private api: ApiService) {
  }

  refreshDevices(): Observable<DeviceDto[]> {
    return this.api.GetGatelockVanLiteProvider_GetDevices_Observable().pipe(
      map(res => JSON.parse(res.data).map((d: any) => new DeviceDto(d))),
      tap(devices => this.devicesSubject.next(devices))
    );
  }

  startDevicesPolling() {
     if (this.devicesPollingSub && !this.devicesPollingSub.closed)
      return; // prevent duplicates

    this.devicesPollingSub = timer(0, AppSettings.DeviceSettings.GetDevicesRefreshRateInSeconds * 1000).pipe(
      exhaustMap(() => //if one request is still running, skip the next tick
        this.refreshDevices().pipe(
          catchError(err => {
            console.error('Devices polling failed', err);
            return EMPTY; // keep polling alive
          })
        )
      )
    )
    .subscribe();
  }

  stopDevicesPolling() {
    this.devicesPollingSub?.unsubscribe();
    this.devicesPollingSub = undefined;
  }


  refreshHistoryEntries(search: HistorySearch): Observable<HistoryEntryDto[]> {
    return this.api.GetGatelockVanLiteProvider_GetHistory_Observable(search).pipe(
      map(res => JSON.parse(res.data).map((l: any) => new HistoryEntryDto(l))),
      tap(logs => this.historyEntriesSubject.next(logs))
    );
  }

  startHistoryEntriesPolling() {
     if (this.historyEntriesPollingSub && !this.historyEntriesPollingSub.closed)
      return; // prevent duplicates

    this.historyEntriesPollingSub = timer(0, AppSettings.DeviceSettings.GetDevicesRefreshRateInSeconds * 1000).pipe(
      switchMap(() => this.searchSubject.pipe(take(1))),
      exhaustMap(search => //if one request is still running, skip the next tick
        this.refreshHistoryEntries(search).pipe(
          catchError(err => {
            console.error('History entries polling failed', err);
            return EMPTY; // keep polling alive
          })
        )
      )
    )
    .subscribe();
  }

  stopHistoryEntriesPolling() {
    this.historyEntriesPollingSub?.unsubscribe();
    this.historyEntriesPollingSub = undefined;
  }

}